import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getEiaCountdown, saveEiaForecast,
  getAllStrategy4Events, addStrategy4Event, updateStrategy4Event,
  deleteStrategy4Event, refreshStrategy4EventsFMP, uploadStrategy4EventsCSV,
  getMarketMoversConstituents, updateMarketMoverConstituent, seedMarketMoversConstituents,
} from '../../api/client';
import { getAIRunLog } from '../../api/ai';

const RAILWAY = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ── helpers ── */
export function SectionCard({ title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div style={{
      background: 'var(--surface)', border: '.5px solid var(--hair)',
      borderRadius: 8, marginBottom: 16, overflow: 'hidden',
    }}>
      {/* Header — always visible, click to toggle */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 18px', cursor: 'pointer', userSelect: 'none',
          borderBottom: open ? '.5px solid var(--hair)' : 'none',
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        <span style={{
          fontSize: 14, color: 'var(--ink-3)', lineHeight: 1,
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform .18s', display: 'inline-block',
        }}>▾</span>
      </div>
      {/* Body — only rendered when expanded */}
      {open && (
        <div style={{ padding: '16px 18px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const INP = {
  background: 'var(--surface-2)', border: '.5px solid var(--hair-strong)',
  borderRadius: 4, color: 'var(--ink)', fontSize: 12,
  padding: '6px 10px', fontFamily: 'var(--font-data)',
  outline: 'none', width: '100%', boxSizing: 'border-box',
};

const BTN = (extra = {}) => ({
  fontSize: 11, fontWeight: 600, padding: '7px 18px', borderRadius: 5,
  border: 'none', cursor: 'pointer', ...extra,
});

/* ══════════════════════════════════════════════════════
   Section 1 — FII / DII Manual Entry
══════════════════════════════════════════════════════ */
export function FiiDiiSection() {
  const today = new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState([{ date: today, fii: '', dii: '' }]);
  const [status, setStatus] = useState(null);   // null | 'saving' | 'ok' | 'err'
  const [msg, setMsg] = useState('');

  const updateRow = (i, field, val) =>
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  const addRow = () => setRows(prev => [...prev, { date: today, fii: '', dii: '' }]);
  const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    const payload = rows
      .filter(r => r.date && (r.fii !== '' || r.dii !== ''))
      .map(r => ({
        date: r.date,
        ...(r.fii !== '' ? { fii: parseFloat(r.fii) } : {}),
        ...(r.dii !== '' ? { dii: parseFloat(r.dii) } : {}),
      }));

    if (!payload.length) {
      setMsg('Fill in at least one row with a date and a value.');
      setStatus('err');
      return;
    }

    setStatus('saving');
    try {
      const res = await fetch(`${RAILWAY}/api/nifty/patch-fii-dii`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: payload }),
      });
      if (res.status === 401 || res.status === 403) {
        setMsg('Not authorized — sign in as owner.');
        setStatus('err');
        return;
      }
      const json = await res.json();
      if (res.ok && json.patched > 0) {
        setMsg(`✓ Saved ${json.patched} row${json.patched > 1 ? 's' : ''} successfully.`);
        setStatus('ok');
      } else {
        setMsg(`Error: ${json.error || JSON.stringify(json)}`);
        setStatus('err');
      }
    } catch (e) {
      setMsg(`Network error: ${e.message}`);
      setStatus('err');
    }
  };

  return (
    <SectionCard
      title="FII / DII Equity Cash Flow"
      subtitle="Manually enter NET values (₹ Cr) for dates where the auto-sync missed data. Values from NSE: nseindia.com/reports/fii-dii"
    >
      {/* Column headers */}
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr 28px', gap: 8, marginBottom: 6, paddingRight: 2 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Date</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>FII/FPI Net (₹ Cr)</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>DII Net (₹ Cr)</div>
        <div/>
      </div>

      {rows.map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr 28px', gap: 8, marginBottom: 8 }}>
          <input
            type="date"
            value={r.date}
            onChange={e => updateRow(i, 'date', e.target.value)}
            style={{ ...INP }}
          />
          <input
            type="number"
            step="0.01"
            placeholder="e.g. -8047.86"
            value={r.fii}
            onChange={e => updateRow(i, 'fii', e.target.value)}
            style={{ ...INP }}
          />
          <input
            type="number"
            step="0.01"
            placeholder="e.g. 3487.10"
            value={r.dii}
            onChange={e => updateRow(i, 'dii', e.target.value)}
            style={{ ...INP }}
          />
          <button
            onClick={() => removeRow(i)}
            disabled={rows.length === 1}
            style={{ ...BTN(), background: 'transparent', border: '.5px solid var(--hair)', color: 'var(--ink-3)', padding: '4px 8px', opacity: rows.length === 1 ? 0.3 : 1 }}
          >×</button>
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <button onClick={addRow} style={{ ...BTN(), background: 'var(--surface-2)', border: '.5px solid var(--hair-strong)', color: 'var(--ink-2)' }}>
          + Add Row
        </button>
        <button
          onClick={handleSubmit}
          disabled={status === 'saving'}
          style={{ ...BTN(), background: 'var(--amber-ink)', color: '#fff', opacity: status === 'saving' ? 0.6 : 1 }}
        >
          {status === 'saving' ? 'Saving…' : 'Save to Database'}
        </button>
        {msg && (
          <span style={{ fontSize: 11, color: status === 'ok' ? 'var(--up-field-ink)' : 'var(--down-field-ink)', fontWeight: 600 }}>
            {msg}
          </span>
        )}
      </div>

      <div style={{ marginTop: 14, padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 5, fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--ink-2)' }}>When to use:</strong> If the auto-sync script missed a trading day (Mac was off all day),
        open <a href="https://www.nseindia.com/reports/fii-dii" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--amber-ink)' }}>NSE FII/DII page</a>,
        note the NET values for FII/FPI and DII, enter them here and click Save.
        Negative = net sellers, Positive = net buyers.
      </div>
    </SectionCard>
  );
}

/* ══════════════════════════════════════════════════════
   Section 2 — EIA Natural Gas Forecast
══════════════════════════════════════════════════════ */
export function EiaForecastSection() {
  const [history, setHistory] = useState([]);
  const [drafts, setDrafts]   = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const [status, setStatus]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getEiaCountdown();
      const weeks = res.last_10_weeks || res.last_5_weeks || [];
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      setHistory(weeks.map(w => {
        const parts = (w.period || '').split('-');
        const label = parts.length === 3
          ? `w/e ${months[+parts[1]-1]} ${parts[2]}`
          : (w.period || '');
        return {
          period:   w.period,
          label,
          actual:   w.change_bcf   ?? null,
          forecast: w.forecast_bcf ?? null,
          surprise: w.surprise_bcf ?? null,
        };
      }));
    } catch (e) {
      setMsg('Failed to load EIA data.');
      setStatus('err');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    const entries = Object.entries(drafts).filter(([, v]) => v.trim() !== '');
    if (!entries.length) { setMsg('No changes to save.'); setStatus('err'); return; }
    setSaving(true);
    setMsg('');
    let saved = 0;
    for (const [period, val] of entries) {
      const num = parseFloat(val);
      if (!isNaN(num)) {
        await saveEiaForecast(period, num);
        saved++;
      }
    }
    setSaving(false);
    setDrafts({});
    setMsg(`✓ Saved ${saved} forecast${saved > 1 ? 's' : ''}.`);
    setStatus('ok');
    load();
  };

  const fmt = v => v == null ? '—' : `${v > 0 ? '+' : ''}${v}`;

  if (loading) return (
    <SectionCard title="EIA Natural Gas Storage — Forecasts">
      <div style={{ color: 'var(--ink-3)', fontSize: 11 }}>Loading…</div>
    </SectionCard>
  );

  return (
    <SectionCard
      title="EIA Natural Gas Storage — Forecasts"
      subtitle="Enter your forecast (Bcf) for upcoming weeks. Negative = bullish draw, Positive = bearish inject."
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ borderBottom: '.5px solid var(--hair)' }}>
            {['Week ending','Actual (Bcf)','Your Forecast (Bcf)','Surprise'].map(h => (
              <th key={h} style={{ textAlign: h === 'Week ending' ? 'left' : 'right', padding: '4px 10px', color: 'var(--ink-3)', fontWeight: 600, fontSize: 11 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {history.map((row, i) => {
            const draftVal = drafts[row.period] !== undefined
              ? drafts[row.period]
              : (row.forecast != null ? String(row.forecast) : '');
            const noActual = row.actual == null || row.actual === 0;
            return (
              <tr key={i} style={{ borderBottom: '.5px solid var(--hair)', background: noActual ? 'var(--surface-2)' : 'transparent' }}>
                <td style={{ padding: '5px 10px', color: 'var(--ink-3)', fontSize: 11 }}>
                  {row.label}
                  {noActual && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'var(--accentbg)', color: 'var(--amber-ink)' }}>UPCOMING</span>}
                </td>
                <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--font-data)', fontWeight: 600, color: 'var(--ink)' }}>
                  {row.actual != null ? fmt(row.actual) : '—'}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="—"
                    value={draftVal}
                    onChange={e => setDrafts(prev => ({ ...prev, [row.period]: e.target.value }))}
                    style={{ ...INP, width: 110, textAlign: 'right' }}
                  />
                </td>
                <td style={{
                  padding: '5px 10px', textAlign: 'right',
                  fontFamily: 'var(--font-data)', fontWeight: row.surprise != null ? 700 : 400,
                  color: row.surprise == null ? 'var(--ink-3)' : row.surprise < 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
                }}>
                  {fmt(row.surprise)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <button
          onClick={handleSave}
          disabled={saving || !Object.keys(drafts).length}
          style={{ ...BTN(), background: 'var(--amber-ink)', color: '#fff', opacity: (saving || !Object.keys(drafts).length) ? 0.5 : 1 }}
        >
          {saving ? 'Saving…' : 'Save Forecasts'}
        </button>
        <button
          onClick={() => { setDrafts({}); setMsg(''); }}
          disabled={!Object.keys(drafts).length}
          style={{ ...BTN(), background: 'var(--surface-2)', border: '.5px solid var(--hair-strong)', color: 'var(--ink-2)', opacity: !Object.keys(drafts).length ? 0.4 : 1 }}
        >
          Cancel
        </button>
        {msg && (
          <span style={{ fontSize: 11, color: status === 'ok' ? 'var(--up-field-ink)' : 'var(--down-field-ink)', fontWeight: 600 }}>
            {msg}
          </span>
        )}
      </div>

      <div style={{ marginTop: 14, padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 5, fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--ink-2)' }}>Source:</strong>{' '}
        <a href="https://in.investing.com/economic-calendar/natural-gas-storage-386" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--amber-ink)' }}>
          Investing.com — Natural Gas Storage
        </a>
        . Enter the consensus forecast before each Thursday release. Rows highlighted in blue are upcoming weeks without an actual yet.
      </div>
    </SectionCard>
  );
}

/* ══════════════════════════════════════════════════════
   Section 3 — Data Sources
══════════════════════════════════════════════════════ */

const SOURCE_META = {
  yahoo:     { label: 'Yahoo Finance',  color: '#6366f1', bg: 'rgba(99,102,241,.13)',   url: 'https://finance.yahoo.com' },
  kite:      { label: 'Kite Connect',   color: '#f59e0b', bg: 'rgba(245,158,11,.13)',  url: 'https://kite.trade/docs/connect/v3/' },
  nse:       { label: 'NSE India',      color: '#10b981', bg: 'rgba(16,185,129,.13)',  url: 'https://www.nseindia.com' },
  nsearch:   { label: 'NSE Archives',   color: '#3b82f6', bg: 'rgba(59,130,246,.13)',  url: 'https://nsearchives.nseindia.com' },
  eia:       { label: 'EIA',            color: '#ef4444', bg: 'rgba(239,68,68,.13)',   url: 'https://www.eia.gov' },
  rss:       { label: 'RSS Feeds',      color: '#8b5cf6', bg: 'rgba(139,92,246,.13)',  url: 'https://feedparser.readthedocs.io' },
  fmp:       { label: 'FMP',            color: '#0ea5e9', bg: 'rgba(14,165,233,.13)',  url: 'https://financialmodelingprep.com/developer/docs' },
  anthropic: { label: 'Claude (Anthropic)', color: '#d97706', bg: 'rgba(217,119,6,.13)', url: 'https://docs.anthropic.com' },
};

// One row per discrete feed. tab + section + widget = full hierarchy.
const ALL_FEEDS = [
  // ══ Dashboard ══
  // Kite — indices & live prices
  { src: 'kite',    tab: 'Dashboard', section: 'Equity Market',           widget: 'NIFTY 50, Bank Nifty, Sensex, India VIX',              detail: 'Kite quote API — live LTP + chg% for all 4 indices',          url: 'https://kite.trade/docs/connect/v3/market-quotes/' },
  { src: 'kite',    tab: 'Dashboard', section: 'Equity Market',           widget: 'GIFT Nifty / Pre-Market signal',                        detail: 'Kite quote API — near-month NIFTY futures LTP',               url: 'https://kite.trade/docs/connect/v3/market-quotes/' },
  { src: 'kite',    tab: 'Dashboard', section: 'Oil & Gas',               widget: 'MCX Nat Gas, MCX Crude Oil',                           detail: 'Kite quote API — MCX:NATGASMINI, MCX:CRUDEOIL',              url: 'https://kite.trade/docs/connect/v3/market-quotes/' },
  { src: 'kite',    tab: 'Dashboard', section: 'Metals',                  widget: 'GOLDBEES, SILVERBEES',                                 detail: 'Kite quote API — NSE ETF quotes',                            url: 'https://kite.trade/docs/connect/v3/market-quotes/' },
  { src: 'kite',    tab: 'Dashboard', section: 'Sector Heatmap',          widget: 'Sector, Thematic & Broad index % change',              detail: 'Kite quote API — all index labels from INDEX_REGISTRY',      url: 'https://kite.trade/docs/connect/v3/market-quotes/' },
  { src: 'kite',    tab: 'Dashboard', section: 'Market Breadth',          widget: 'Advances / Declines — NIFTY 50 & F&O universe',        detail: 'Kite fo-stocks cache — chg% per stock to classify adv/dec', url: 'https://kite.trade/docs/connect/v3/market-quotes/' },
  { src: 'kite',    tab: 'Dashboard', section: 'F&O Movers',              widget: 'Top gainers, losers, and volume leaders',               detail: 'Kite fo-stocks quote cache — sorted by chg% and volume',    url: 'https://kite.trade/docs/connect/v3/market-quotes/' },
  // Yahoo Finance — commodity & fx spot prices
  { src: 'yahoo',   tab: 'Dashboard', section: 'Oil & Gas',               widget: 'US NG1 (NG=F), US Oil WTI (CL=F)',                     detail: 'yfinance tickers: NG=F, CL=F — daily close',                 url: 'https://finance.yahoo.com/commodities' },
  { src: 'yahoo',   tab: 'Dashboard', section: 'Metals',                  widget: 'Gold XAUUSD (GC=F), Silver XAGUSD (SI=F)',             detail: 'yfinance tickers: GC=F, SI=F — daily close',                 url: 'https://finance.yahoo.com/commodities' },
  { src: 'yahoo',   tab: 'Dashboard', section: 'Currency & Crypto',       widget: 'USD/INR (INR=X), Bitcoin (BTC-USD)',                   detail: 'yfinance tickers: INR=X, BTC-USD — daily close',             url: 'https://finance.yahoo.com/currencies' },
  // NSE Archives — 52-week extremes (via bhavcopy)
  { src: 'nsearch', tab: 'Dashboard', section: '52-Week Extremes Today',  widget: '52-week highs & lows for F&O stocks',                  detail: 'Daily bhavcopy — 52W H/L fields; chg% topped up from Kite',  url: 'https://nsearchives.nseindia.com/content/historical/EQUITIES/' },
  // NSE India — FII/DII flows (Mac cron, Railway is blocked)
  { src: 'nse',     tab: 'Dashboard', section: 'FII/DII Flow',            widget: 'FII & DII net equity cash flows (₹ Cr)',                detail: 'Mac cron script → nseindia.com/reports/fii-dii (daily NET)', url: 'https://www.nseindia.com/reports/fii-dii' },
  // EIA
  { src: 'eia',     tab: 'Dashboard', section: 'EIA Natural Gas Report',  widget: 'Weekly storage change, countdown & history (Bcf)',     detail: 'EIA API v2 — /natural-gas/stor/wkly/data/',                  url: 'https://api.eia.gov/v2/natural-gas/stor/wkly/data/' },
  // RSS news aggregation
  { src: 'rss',     tab: 'Dashboard', section: 'Market News',             widget: 'News feed — Indian Equity, Energy, Macro, Global, FII', detail: 'feedparser — Moneycontrol, ET, Reuters, Bloomberg, OilPrice, NGI, BS, LiveMint', url: 'https://www.moneycontrol.com/rss/latestnews.xml' },

  // ══ NIFTY Tab ══
  { src: 'kite',    tab: 'NIFTY', section: 'Header',                      widget: 'NIFTY 50 live price & market status badge',            detail: 'Kite quote API — NSE:NIFTY 50 LTP',                          url: 'https://kite.trade/docs/connect/v3/market-quotes/' },
  { src: 'nsearch', tab: 'NIFTY', section: 'Market Trend Charts',         widget: 'C1 — NIFTY Close + Futures Build-Up',                  detail: 'NSE Archives bhavcopy (OHLCV) + futures OI stored via backfill', url: 'https://nsearchives.nseindia.com/content/historical/EQUITIES/' },
  { src: 'nsearch', tab: 'NIFTY', section: 'Market Trend Charts',         widget: 'C2 — India VIX',                                       detail: 'NSE Archives — VIX data stored in nifty_daily DB table',      url: 'https://nsearchives.nseindia.com/content/indices/' },
  { src: 'kite',    tab: 'NIFTY', section: 'Market Trend Charts',         widget: 'C3 — PCR Near Expiry',                                 detail: 'Kite option chain OI — near & near+next expiry PCR computed daily', url: 'https://kite.trade/docs/connect/v3/instruments/' },
  { src: 'nsearch', tab: 'NIFTY', section: 'Market Trend Charts',         widget: 'C4 — FII+PRO % Net Long',                              detail: 'NSE Archives — participant-wise index F&O OI (fii_oi table)', url: 'https://nsearchives.nseindia.com/archives/fo/participant_oi/' },
  { src: 'nsearch', tab: 'NIFTY', section: 'Market Trend Charts',         widget: 'C5 — NIFTY 50 Breadth %',                             detail: 'NSE Archives bhavcopy — adv/dec counted daily into nifty_daily', url: 'https://nsearchives.nseindia.com/content/historical/EQUITIES/' },
  { src: 'nse',     tab: 'NIFTY', section: 'Market Trend Charts',         widget: 'C6 — DII + FII + FII Deriv Flows (₹ Cr)',              detail: 'Mac cron → nseindia.com/reports/fii-dii — stored in fii_dii table', url: 'https://www.nseindia.com/reports/fii-dii' },
  { src: 'kite',    tab: 'NIFTY', section: 'Data Table',                  widget: 'PCR Near / N+Nx columns',                              detail: 'Kite option chain OI — stored in nifty_daily',               url: 'https://kite.trade/docs/connect/v3/instruments/' },
  { src: 'nsearch', tab: 'NIFTY', section: 'Data Table',                  widget: 'NIFTY OHLCV, Futures OI, OI Chg%, Build, Breadth, VIX', detail: 'NSE Archives bhavcopy + participant OI — via nifty_daily backfill', url: 'https://nsearchives.nseindia.com/content/historical/EQUITIES/' },
  { src: 'nse',     tab: 'NIFTY', section: 'Data Table',                  widget: 'DII Stks, FII Stks, FII Fut columns',                  detail: 'Mac cron → nseindia.com/reports/fii-dii — stored in fii_dii', url: 'https://www.nseindia.com/reports/fii-dii' },
  { src: 'nsearch', tab: 'NIFTY', section: 'Data Table',                  widget: '% Net Long — FII F+O, FII+PRO, Client',                detail: 'NSE Archives — participant-wise index F&O OI ratios',         url: 'https://nsearchives.nseindia.com/archives/fo/participant_oi/' },

  // ══ Options Tab ══
  { src: 'kite', tab: 'Options', section: 'Option Chain',                  widget: 'CE/PE OI, LTP, Greeks, Max Pain, PCR',                 detail: 'Kite option chain API — requires daily OAuth login',          url: 'https://kite.trade/docs/connect/v3/instruments/' },
  { src: 'kite', tab: 'Options', section: 'OI Analysis',                   widget: 'OI heatmap, PCR/CPR chart, strike OI history',         detail: 'Kite option chain — snapshotted every 5 min during market hours', url: 'https://kite.trade/docs/connect/v3/instruments/' },
  { src: 'kite', tab: 'Options', section: 'Strategy Builder',              widget: 'Instrument lookup, LTP for strategy legs',              detail: 'Kite instruments cache + quote API',                          url: 'https://kite.trade/docs/connect/v3/instruments/' },

  // ══ Relative Strength Tab ══
  { src: 'yahoo', tab: 'Relative Strength', section: 'Broad Asset Classes', widget: 'NIFTY 50, Gold, USD/INR, G-Sec 10Y — RS cards & charts', detail: 'yfinance — ^NSEI, GC=F, INR=X; G-Sec via proxy ticker',    url: 'https://finance.yahoo.com' },
  { src: 'yahoo', tab: 'Relative Strength', section: 'RS Ratio Bar Chart',  widget: 'All asset classes + sectors — RS vs NIFTY 50',          detail: 'yfinance OHLCV for all tracked assets and sector ETFs',       url: 'https://finance.yahoo.com' },
  { src: 'yahoo', tab: 'Relative Strength', section: 'RS Ratio Charts',     widget: 'Individual RS time-series charts (all assets + sectors)', detail: 'yfinance OHLCV — same data set, displayed as line charts',   url: 'https://finance.yahoo.com' },
  { src: 'yahoo', tab: 'Relative Strength', section: 'Top-Down (Sector › Stocks)', widget: 'F&O stocks RS ranked within each sector',        detail: 'yfinance OHLCV for F&O universe vs sector benchmark',         url: 'https://finance.yahoo.com' },
  { src: 'yahoo', tab: 'Relative Strength', section: 'F&O Stock Rankings',  widget: 'RS & price performance table — all F&O stocks',         detail: 'yfinance OHLCV — /api/rs/fo-stocks endpoint',                url: 'https://finance.yahoo.com' },
  { src: 'yahoo', tab: 'Relative Strength', section: 'Multi-TF RS — Asset Classes & Sectors', widget: '1W / 1M / 3M / 6M / 1Y RS table', detail: 'yfinance OHLCV — same asset set, multi-period returns',       url: 'https://finance.yahoo.com' },
  { src: 'yahoo', tab: 'Relative Strength', section: 'Multi-TF RS — F&O Stocks', widget: '1W / 1M / 3M / 6M / 1Y RS table for F&O stocks', detail: 'yfinance OHLCV — /api/rs/fo-stocks multi-period returns',    url: 'https://finance.yahoo.com' },

  // ══ Sector Rotation Tab ══
  { src: 'yahoo', tab: 'Sector Rotation', section: 'Chart 1 — JdK RRG (Sectors vs NIFTY 50)', widget: 'Sector RRG — RS-Ratio & RS-Momentum trails', detail: 'yfinance OHLCV — sector ETFs & ^NSEI benchmark; JdK algorithm', url: 'https://finance.yahoo.com' },
  { src: 'yahoo', tab: 'Sector Rotation', section: 'Chart 2 — WMA RRG (F&O Stocks vs Sector)', widget: 'F&O stock RRG — RS vs their parent sector', detail: 'yfinance OHLCV — full F&O universe vs sector index benchmarks', url: 'https://finance.yahoo.com' },
  { src: 'yahoo', tab: 'Sector Rotation', section: 'Chart 3 — WMA RRG (F&O Stocks vs NIFTY 50)', widget: 'F&O stock RRG — RS vs NIFTY 50', detail: 'yfinance OHLCV — full F&O universe vs ^NSEI benchmark',      url: 'https://finance.yahoo.com' },

  // ══ Scanner Tab ══
  { src: 'kite', tab: 'Scanner', section: 'Signal Table',                  widget: 'Bullish / Bearish EMA crossover signals',               detail: 'Kite historical_data API — per-symbol candles for computation', url: 'https://kite.trade/docs/connect/v3/historical/' },

  // ══ NSE Directory Tab ══
  { src: 'nsearch', tab: 'NSE Directory', section: 'Stock Directory',      widget: 'Stock list — symbol, company, ISIN, sector, F&O flag', detail: 'NSE Archives index CSVs — ind_nifty100list.csv etc.',         url: 'https://nsearchives.nseindia.com/content/indices/' },
  { src: 'nsearch', tab: 'NSE Directory', section: 'Index Memberships',    widget: 'Index constituent lists (NIFTY 50, 100, sector indices)', detail: 'NSE Archives — ind_*list.csv files for each index',          url: 'https://nsearchives.nseindia.com/content/indices/' },

  // ══ Market Movers / Index Drivers Tab ══
  { src: 'kite',    tab: 'Market Movers', section: 'Index Drivers',        widget: 'NIFTY 50, SENSEX, Bank NIFTY constituent contributions', detail: 'Kite quote API — per-stock chg% × NSE index weight → CONTRIB% and %OF MOVE', url: 'https://kite.trade/docs/connect/v3/market-quotes/' },

  // ══ Macro Events Calendar Tab ══
  { src: 'fmp',     tab: 'Events Calendar', section: 'US Macro Events',    widget: 'Fed, CPI, NFP, PCE, FOMC — US economic calendar',         detail: 'FMP /economic_calendar API — refreshed on-demand via owner-initiated sync', url: 'https://financialmodelingprep.com/developer/docs#economic-calendar' },
  { src: 'nse',     tab: 'Events Calendar', section: 'Indian Events',      widget: 'RBI MPC, Budget, expiry dates, NSE scheduled events',      detail: 'Manually curated + NSE announcements; stored in strategy4_events', url: 'https://www.nseindia.com' },

  // ══ AI Summary Tab ══
  { src: 'anthropic', tab: 'AI Summary', section: 'Morning Digest',        widget: 'AI-generated market snapshot (Dashboard context)',         detail: 'Claude API (claude-sonnet-4-6) — generated daily at 08:30 IST from cached market data', url: 'https://docs.anthropic.com' },
  { src: 'anthropic', tab: 'AI Summary', section: 'NIFTY Daily Prediction', widget: 'NIFTY directional call with factor analysis',             detail: 'Claude API — generated on-demand or daily at 08:30 IST; references nifty_daily + signal_log', url: 'https://docs.anthropic.com' },
  { src: 'rss',     tab: 'AI Summary', section: 'News Archive',            widget: 'Full news feed — Indian Equity, Energy, Macro, Global',    detail: 'feedparser + AI impact scoring via Haiku — stored in news_filtered', url: 'https://www.moneycontrol.com/rss/latestnews.xml' },
];

function SourceBadge({ srcKey }) {
  const m = SOURCE_META[srcKey];
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 700,
      padding: '2px 6px', borderRadius: 3,
      background: m.bg, color: m.color,
      border: `.5px solid ${m.color}44`,
      letterSpacing: '.04em', whiteSpace: 'nowrap',
    }}>{m.label}</span>
  );
}

const TH = { padding: '5px 10px', fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '.5px solid var(--hair)', textAlign: 'left', background: 'var(--surface-2)' };
const TD = { padding: '6px 10px', fontSize: 11, color: 'var(--ink)', borderBottom: '.5px solid var(--hair)', verticalAlign: 'middle' };

export function DataSourcesSection() {
  // ── Table 1: Group by source ──
  const bySource = useMemo(() => {
    const map = {};
    for (const src of Object.keys(SOURCE_META)) {
      map[src] = ALL_FEEDS.filter(f => f.src === src);
    }
    return map;
  }, []);

  // ── Table 2: Group by Tab, then Section ──
  // Structure: { tab: { section: [feeds] } }
  const byTerminal = useMemo(() => {
    const map = {};
    for (const f of ALL_FEEDS) {
      if (!map[f.tab]) map[f.tab] = {};
      if (!map[f.tab][f.section]) map[f.tab][f.section] = [];
      map[f.tab][f.section].push(f);
    }
    return map;
  }, []);

  return (
    <SectionCard
      title="Data Sources"
      subtitle="All external data feeds powering this terminal — last updated June 2026."
    >
      {/* ── Table 1: By Source ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 8, marginTop: 2 }}>
        By Source
      </div>
      <div style={{ overflowX: 'auto', marginBottom: 28 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ ...TH, width: 120 }}>Source</th>
              <th style={{ ...TH, width: 110 }}>Tab</th>
              <th style={{ ...TH, width: 160 }}>Section</th>
              <th style={TH}>Widget / Feed</th>
              <th style={TH}>Detail</th>
              <th style={{ ...TH, width: 42, textAlign: 'center' }}>↗</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(bySource).map(([srcKey, feeds]) =>
              feeds.map((f, i) => (
                <tr key={`${srcKey}-${i}`} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,.03)' }}>
                  {i === 0 && (
                    <td rowSpan={feeds.length} style={{ ...TD, verticalAlign: 'top', paddingTop: 10, borderRight: '.5px solid var(--hair)' }}>
                      <SourceBadge srcKey={srcKey} />
                    </td>
                  )}
                  <td style={{ ...TD, color: 'var(--ink-3)', fontSize: 11, fontWeight: 600 }}>{f.tab}</td>
                  <td style={{ ...TD, color: 'var(--ink-3)', fontSize: 11 }}>{f.section}</td>
                  <td style={{ ...TD, fontWeight: 500 }}>{f.widget}</td>
                  <td style={{ ...TD, color: 'var(--ink-3)', fontSize: 11 }}>{f.detail}</td>
                  <td style={{ ...TD, textAlign: 'center' }}>
                    <a href={f.url} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 11, color: 'var(--amber-ink)', textDecoration: 'none', fontWeight: 600 }}>↗</a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Table 2: By Terminal (tab › section grouping) ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 8 }}>
        By Terminal
      </div>
      <div style={{ overflowX: 'auto' }}>
        {Object.entries(byTerminal).map(([tab, sections]) => (
          <div key={tab} style={{ marginBottom: 18 }}>
            {/* Tab header */}
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--amber-ink)', textTransform: 'uppercase',
              letterSpacing: '.06em', padding: '4px 0', marginBottom: 4,
              borderBottom: '1.5px solid var(--amber-ink)', display: 'inline-block', minWidth: 120 }}>
              {tab}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ ...TH, width: 180 }}>Section</th>
                  <th style={TH}>Widget / Feed</th>
                  <th style={{ ...TH, width: 120 }}>Source</th>
                  <th style={TH}>Detail</th>
                  <th style={{ ...TH, width: 42, textAlign: 'center' }}>↗</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(sections).map(([section, feeds]) =>
                  feeds.map((f, i) => (
                    <tr key={`${section}-${i}`} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,.03)' }}>
                      {i === 0 && (
                        <td rowSpan={feeds.length} style={{ ...TD, verticalAlign: 'top', paddingTop: 8,
                          fontSize: 11, color: 'var(--ink-3)', fontWeight: 600, borderRight: '.5px solid var(--hair)' }}>
                          {section}
                        </td>
                      )}
                      <td style={{ ...TD, fontWeight: 500 }}>{f.widget}</td>
                      <td style={{ ...TD }}><SourceBadge srcKey={f.src} /></td>
                      <td style={{ ...TD, color: 'var(--ink-3)', fontSize: 11 }}>{f.detail}</td>
                      <td style={{ ...TD, textAlign: 'center' }}>
                        <a href={f.url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 11, color: 'var(--amber-ink)', textDecoration: 'none', fontWeight: 600 }}>↗</a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 6, padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 5, fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--ink-2)' }}>Note:</strong> NSE India (FII/DII flows) is fetched by a Mac cron script —
        Railway servers are blocked by nseindia.com. NSE Archives and EIA are fetched directly on Railway.
        Yahoo Finance, Kite Connect, and RSS feeds also run on Railway.
      </div>
    </SectionCard>
  );
}

/* ══════════════════════════════════════════════════════
   Macro Events Calendar
══════════════════════════════════════════════════════ */
const EVENT_TYPE_OPTIONS = ['FOMC','RBI_MPC','US_CPI','US_NFP','IN_BUDGET','IN_ELECTION','NIFTY_MONTHLY_EXPIRY','OTHER'];
const EVENT_TYPE_LABELS  = {
  FOMC:'FOMC Rate Decision', RBI_MPC:'RBI MPC', US_CPI:'US CPI',
  US_NFP:'US NFP', IN_BUDGET:'Indian Budget', IN_ELECTION:'Indian Election',
  NIFTY_MONTHLY_EXPIRY:'NIFTY Monthly Expiry', OTHER:'Other',
};
const EVENT_IMPACT_COLORS = { HIGH:'#ef4444', MEDIUM:'#f59e0b', LOW:'#94a3b8' };

const BLANK_FORM = { event_date:'', event_type:'FOMC', title:'FOMC Rate Decision', impact:'HIGH', region:'', category:'', notes:'' };

function MacroEventsSection() {
  const [events,    setEvents]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState({ region:'', impact:'', event_type:'' });
  const [editId,    setEditId]    = useState(null);   // null = add mode, number = edit mode
  const [form,      setForm]      = useState({ ...BLANK_FORM });
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [status,    setStatus]    = useState(null);   // null | 'ok' | 'err' | string
  const [refreshing,setRefreshing]= useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllStrategy4Events({
        region:     filter.region    || undefined,
        impact:     filter.impact    || undefined,
      });
      let evts = res.events || [];
      if (filter.event_type) evts = evts.filter(e => e.event_type === filter.event_type);
      setEvents(evts);
    } catch (e) {
      setStatus(`Load error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditId(null);
    setForm({ ...BLANK_FORM });
    setShowForm(true);
  };
  const openEdit = (e) => {
    setEditId(e.id);
    setForm({
      event_date: e.event_date || '', event_type: e.event_type || 'OTHER',
      title:      e.title || '',      impact:     e.impact || 'HIGH',
      region:     e.region || '',     category:   e.category || '',
      notes:      e.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.event_date || !form.title) return;
    setSaving(true);
    setStatus(null);
    try {
      if (editId != null) {
        await updateStrategy4Event(editId, form);
      } else {
        await addStrategy4Event(form);
      }
      setShowForm(false);
      setStatus('ok');
      await load();
    } catch (e) {
      setStatus(`Save error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await deleteStrategy4Event(id);
      await load();
    } catch (e) {
      setStatus(`Delete error: ${e.message}`);
    }
  };

  const handleFMPRefresh = async () => {
    const fmpKey = localStorage.getItem('fmp_api_key') || null;
    if (!fmpKey && !window.confirm('No FMP key saved in Settings — use env var FMP_API_KEY on the server?')) return;
    setRefreshing(true);
    setStatus(null);
    try {
      const res = await refreshStrategy4EventsFMP(fmpKey || null);
      setStatus(`FMP refresh: ${res.inserted} new events imported (${res.total_fmp_items} total from API)`);
      await load();
    } catch (e) {
      setStatus(`FMP error: ${e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setStatus(null);
    try {
      const res = await uploadStrategy4EventsCSV(file);
      setStatus(`CSV upload: ${res.inserted} events imported${res.error_count ? `, ${res.error_count} errors` : ''}`);
      await load();
    } catch (err) {
      setStatus(`CSV error: ${err.message}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Group events by month for display
  const grouped = events.reduce((acc, e) => {
    const month = (e.event_date || '').slice(0, 7);
    if (!acc[month]) acc[month] = [];
    acc[month].push(e);
    return acc;
  }, {});

  return (
    <SectionCard
      title="Macro Events Calendar"
      subtitle="All scheduled economic events for Strategy 4 AI prediction context. Seeded events re-appear on backend restart."
    >
      {/* ── Toolbar ── */}
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:12 }}>
        <button onClick={openAdd} style={{ ...BTN(), background:'var(--amber-ink)', color:'#fff', fontSize:11 }}>
          + Add Event
        </button>

        {/* Filters */}
        {[
          ['event_type', 'All Types', EVENT_TYPE_OPTIONS, EVENT_TYPE_LABELS],
          ['impact',     'All Impacts', ['HIGH','MEDIUM','LOW'], {}],
          ['region',     'All Regions', ['US','INDIA','GLOBAL','EU','CHINA','JAPAN'], {}],
        ].map(([key, placeholder, opts, labels]) => (
          <select key={key} value={filter[key]}
            onChange={e => setFilter(f => ({ ...f, [key]: e.target.value }))}
            style={{ ...INP, width:'auto', fontSize:11, padding:'4px 8px' }}>
            <option value="">{placeholder}</option>
            {opts.map(o => <option key={o} value={o}>{labels[o] || o}</option>)}
          </select>
        ))}

        {/* FMP refresh — key set in Settings > Connections */}
        <div style={{ display:'flex', gap:4, marginLeft:'auto', alignItems:'center' }}>
          <button onClick={handleFMPRefresh} disabled={refreshing}
            style={{ ...BTN(), background:'var(--surface-3)', color:'var(--ink-2)', border:'.5px solid var(--hair-strong)', fontSize:11, opacity:refreshing?0.6:1 }}>
            {refreshing ? '⏳ Refreshing…' : '↻ FMP Refresh'}
          </button>
          <label style={{ ...BTN(), background:'var(--surface-3)', color:'var(--ink-2)', border:'.5px solid var(--hair-strong)', fontSize:11, cursor:'pointer' }}>
            {uploading ? '⏳ Uploading…' : '↑ Upload CSV'}
            <input ref={fileRef} type="file" accept=".csv" onChange={handleCSVUpload}
              style={{ display:'none' }} disabled={uploading}/>
          </label>
        </div>
      </div>

      {/* Status message */}
      {status && status !== 'ok' && (
        <div style={{ marginBottom:8, padding:'6px 10px', borderRadius:4, fontSize:11,
          background:'rgba(99,102,241,.08)', border:'.5px solid rgba(99,102,241,.25)', color:'var(--ink)' }}>
          {status}
        </div>
      )}
      {status === 'ok' && (
        <div style={{ marginBottom:8, fontSize:11, color:'var(--up-field-ink)' }}>✓ Saved</div>
      )}

      {/* CSV format hint */}
      <div style={{ fontSize:11, color:'var(--ink-3)', marginBottom:10, opacity:0.7 }}>
        CSV columns: <code>event_date, event_type, title, impact</code> (required) + <code>region, category, notes</code> (optional)
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div style={{
          marginBottom:12, padding:'12px 14px',
          background:'var(--surface-3)', border:'.5px solid var(--hair-strong)', borderRadius:6,
        }}>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--ink)', marginBottom:10 }}>
            {editId != null ? 'Edit Event' : 'Add Event'}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'130px 1fr 90px 90px', gap:8, marginBottom:8 }}>
            <Field label="Date">
              <input type="date" value={form.event_date} onChange={e => f('event_date',e.target.value)} style={INP}/>
            </Field>
            <Field label="Title">
              <input type="text" value={form.title} onChange={e => f('title',e.target.value)}
                placeholder="e.g. FOMC Rate Decision" style={INP}/>
            </Field>
            <Field label="Impact">
              <select value={form.impact} onChange={e => f('impact',e.target.value)} style={INP}>
                {['HIGH','MEDIUM','LOW'].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Type">
              <select value={form.event_type} onChange={e => {
                f('event_type', e.target.value);
                f('title', EVENT_TYPE_LABELS[e.target.value] || form.title);
              }} style={INP}>
                {EVENT_TYPE_OPTIONS.map(v => <option key={v} value={v}>{EVENT_TYPE_LABELS[v] || v}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'90px 1fr 1fr', gap:8, marginBottom:10 }}>
            <Field label="Region (opt)">
              <input type="text" value={form.region} onChange={e => f('region',e.target.value)}
                placeholder="US / INDIA…" style={INP}/>
            </Field>
            <Field label="Category (opt)">
              <input type="text" value={form.category} onChange={e => f('category',e.target.value)}
                placeholder="MONETARY_POLICY…" style={INP}/>
            </Field>
            <Field label="Notes (opt)">
              <input type="text" value={form.notes} onChange={e => f('notes',e.target.value)}
                placeholder="e.g. Expected: hold at 6.5%" style={INP}/>
            </Field>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={handleSave} disabled={saving || !form.event_date || !form.title}
              style={{ ...BTN(), background:'var(--amber-ink)', color:'#fff', opacity:saving?0.6:1 }}>
              {saving ? 'Saving…' : editId != null ? 'Update Event' : 'Add Event'}
            </button>
            <button onClick={() => setShowForm(false)}
              style={{ ...BTN(), background:'var(--surface-2)', color:'var(--ink-3)', border:'.5px solid var(--hair-strong)' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Events table */}
      {loading ? (
        <div style={{ fontSize:11.5, color:'var(--ink-3)', padding:'12px 0' }}>Loading events…</div>
      ) : events.length === 0 ? (
        <div style={{ fontSize:11.5, color:'var(--ink-3)', padding:'12px 0' }}>No events match the current filters.</div>
      ) : (
        <div>
          <div style={{ fontSize:11, color:'var(--ink-3)', marginBottom:6 }}>
            {events.length} event{events.length !== 1 ? 's' : ''}
          </div>
          {Object.entries(grouped).sort(([a],[b]) => a.localeCompare(b)).map(([month, evts]) => (
            <div key={month} style={{ marginBottom:10 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--ink-3)', textTransform:'uppercase',
                letterSpacing:'.06em', marginBottom:4 }}>
                {new Date(month + '-15').toLocaleString('en', { month:'long', year:'numeric' })}
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                <tbody>
                  {evts.map(e => (
                    <tr key={e.id} style={{ borderBottom:'.5px solid var(--hair)' }}>
                      <td style={{ padding:'4px 8px', color:'var(--ink-3)', whiteSpace:'nowrap', width:90 }}>
                        {e.event_date}
                      </td>
                      <td style={{ padding:'4px 8px', whiteSpace:'nowrap', width:80 }}>
                        <span style={{
                          fontSize:11.5, padding:'1px 5px', borderRadius:3, fontWeight:700,
                          color: EVENT_IMPACT_COLORS[e.impact] || 'var(--ink-3)',
                          background: `${EVENT_IMPACT_COLORS[e.impact] || '#94a3b8'}18`,
                        }}>{e.impact}</span>
                      </td>
                      <td style={{ padding:'4px 8px', fontWeight:600, color:'var(--ink)' }}>
                        {e.title}
                        {e.notes && <span style={{ fontSize:11, color:'var(--ink-3)', marginLeft:6 }}>— {e.notes}</span>}
                      </td>
                      <td style={{ padding:'4px 8px', color:'var(--ink-3)', fontSize:11, whiteSpace:'nowrap' }}>
                        {e.event_type}
                      </td>
                      <td style={{ padding:'4px 8px', color:'var(--ink-3)', fontSize:11, whiteSpace:'nowrap' }}>
                        {e.region}{e.category ? ` · ${e.category}` : ''}
                      </td>
                      <td style={{ padding:'4px 8px', whiteSpace:'nowrap' }}>
                        <span style={{ fontSize:11, padding:'1px 5px', borderRadius:3,
                          background:'var(--surface-3)', color:'var(--ink-3)' }}>
                          {e.source}
                        </span>
                      </td>
                      <td style={{ padding:'4px 8px', whiteSpace:'nowrap' }}>
                        <button onClick={() => openEdit(e)}
                          style={{ fontSize:11.5, padding:'2px 7px', borderRadius:3, cursor:'pointer',
                            background:'var(--surface-3)', border:'.5px solid var(--hair-strong)', color:'var(--ink-3)', marginRight:4 }}>
                          Edit
                        </button>
                        <button onClick={() => handleDelete(e.id)}
                          style={{ fontSize:11.5, padding:'2px 7px', borderRadius:3, cursor:'pointer',
                            background:'rgba(239,68,68,.08)', border:'.5px solid rgba(239,68,68,.25)', color:'var(--down-field-ink)' }}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

/* ══════════════════════════════════════════════════════
   Section — Sector Assignments
══════════════════════════════════════════════════════ */
const SECTOR_OPTIONS = [
  'Automobile', 'Aviation', 'Banking', 'Cement', 'Chemicals',
  'Conglomerates', 'Consumer Durables', 'Energy', 'FMCG',
  'Financial Services', 'Healthcare', 'IT', 'Infrastructure',
  'Media & Entertainment', 'Metals', 'Pharmaceuticals',
  'Ports & Logistics', 'Real Estate', 'Retail', 'Technology', 'Telecom',
];

export function SectorAssignmentsSection() {
  const [pending,   setPending]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState({});   // { symbol: true }
  const [choices,   setChoices]   = useState({});   // { symbol: sectorStr }
  const [saved,     setSaved]     = useState({});   // { symbol: true }

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${RAILWAY}/api/admin/sector-assignments`, { credentials: 'include' });
      if (res.status === 401 || res.status === 403) { setPending([]); setLoading(false); return; }
      const json = await res.json();
      setPending(json.pending || []);
    } catch { setPending([]); }
    finally  { setLoading(false); }
  };

  useEffect(() => { fetchPending(); }, []);

  const handleSave = async (symbol) => {
    const sector = choices[symbol];
    if (!sector) return;
    setSaving(s => ({ ...s, [symbol]: true }));
    try {
      const res = await fetch(`${RAILWAY}/api/admin/sector-assignments/${encodeURIComponent(symbol)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sector }),
      });
      if (res.ok) {
        setSaved(s => ({ ...s, [symbol]: true }));
        setTimeout(() => {
          setPending(p => p.filter(x => x.symbol !== symbol));
          setSaved(s => { const n = { ...s }; delete n[symbol]; return n; });
        }, 1200);
      }
    } catch { /* ignore */ }
    finally { setSaving(s => ({ ...s, [symbol]: false })); }
  };

  if (loading) return null;   // Don't render the section if no pending items and still loading
  if (!loading && pending.length === 0) return null;  // Hide entirely when nothing pending

  return (
    <SectionCard
      title={`⚠ Sector Assignment Required (${pending.length})`}
      subtitle="These stocks were added to the F&O universe but have no sector assigned. They appear as '-' everywhere until reviewed."
      defaultOpen={true}>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {pending.map(item => {
          const sym   = item.symbol.replace('.NS', '');
          const isDone = saved[item.symbol];
          return (
            <div key={item.symbol} style={{
              display: 'grid',
              gridTemplateColumns: '110px 1fr 160px 80px',
              gap: 8, alignItems: 'center',
              padding: '6px 10px',
              background: 'var(--surface-2)',
              borderRadius: 5,
              border: isDone ? '.5px solid var(--up-field-ink)' : '.5px solid var(--hair)',
              transition: 'border-color .2s',
            }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink)' }}>{sym}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                NSE: {item.nse_sector || '—'}
              </div>
              <select
                value={choices[item.symbol] || ''}
                onChange={e => setChoices(c => ({ ...c, [item.symbol]: e.target.value }))}
                style={{ ...INP, fontSize: 11, padding: '5px 8px' }}>
                <option value="">— pick sector —</option>
                {SECTOR_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                onClick={() => handleSave(item.symbol)}
                disabled={!choices[item.symbol] || saving[item.symbol] || isDone}
                style={{
                  ...BTN(),
                  background: isDone ? 'var(--up-field-ink)' : 'var(--amber-ink)',
                  color: '#fff',
                  opacity: (!choices[item.symbol] || saving[item.symbol]) ? .5 : 1,
                  fontSize: 11,
                }}>
                {isDone ? '✓ Saved' : saving[item.symbol] ? '…' : 'Assign'}
              </button>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.6 }}>
        Once assigned, the stock appears in Sector Rotation and RS charts from the next data refresh.
      </div>
    </SectionCard>
  );
}


/* ══════════════════════════════════════════════════════
   Section — Scheduler Logs
══════════════════════════════════════════════════════ */
const JOB_LABELS = {
  fo_universe_refresh: 'F&O Universe Refresh',
  daily_pipeline:      'Daily Pipeline',
  nse_eod_refresh:     'NSE EOD Refresh',
  fii_dii_fetch:       'FII/DII Cash Fetch',
  contrarian_sip:      'Contrarian SIP',
  directory_sync:      'Directory Sync',
};

export function SchedulerLogsSection() {
  const [logs,       setLogs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [trigResult, setTrigResult] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${RAILWAY}/api/admin/logs?limit=50`, { credentials: 'include' });
      if (res.status === 401 || res.status === 403) { setLogs([]); setLoading(false); return; }
      const json = await res.json();
      setLogs(json.logs || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  const handleTrigger = async () => {
    setTriggering(true);
    setTrigResult(null);
    try {
      const res  = await fetch(`${RAILWAY}/api/admin/trigger/fo-universe-refresh`, { method: 'POST', credentials: 'include' });
      const json = await res.json();
      setTrigResult(json);
      await fetchLogs();
    } catch (e) {
      setTrigResult({ status: 'error', message: String(e) });
    } finally {
      setTriggering(false);
    }
  };

  const fmtTime = iso => {
    try {
      return new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
      }) + ' IST';
    } catch { return iso; }
  };

  const statusStyle = s => ({
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 3,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    background: s === 'success' ? 'rgba(0,200,100,.15)' : 'rgba(230,70,70,.15)',
    color:      s === 'success' ? 'var(--up-field-ink)'           : 'var(--down-field-ink)',
  });

  return (
    <SectionCard
      title="Scheduler Logs"
      subtitle="Automated job run history — most recent 50 entries">

      {/* Manual trigger row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <button
          onClick={handleTrigger}
          disabled={triggering}
          style={{ ...BTN(), minWidth: 180, opacity: triggering ? .6 : 1 }}>
          {triggering ? 'Running…' : '▶ Run F&O Universe Refresh Now'}
        </button>
        <button onClick={fetchLogs} style={{ ...BTN(), minWidth: 80 }}>↻ Refresh</button>
        {trigResult && (
          <span style={{
            fontSize: 11,
            color: trigResult.status === 'success' ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
            fontWeight: 600,
          }}>
            {trigResult.status === 'success'
              ? `✓ ${trigResult.message}`
              : `✗ ${trigResult.message}`}
          </span>
        )}
      </div>

      {/* Logs table */}
      {loading ? (
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Loading…</div>
      ) : logs.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          No scheduler runs recorded yet. Logs appear here after the first job runs.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                {['Job', 'Run At (IST)', 'Status', 'Summary'].map(h => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '5px 8px',
                    borderBottom: '.5px solid var(--hair)',
                    color: 'var(--ink-3)', fontWeight: 700, fontSize: 11,
                    textTransform: 'uppercase', letterSpacing: '.05em',
                    whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={log.id} style={{
                  background: i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,.04)',
                }}>
                  <td style={{ padding: '5px 8px', whiteSpace: 'nowrap', color: 'var(--ink)', fontWeight: 600 }}>
                    {JOB_LABELS[log.job_id] || log.job_id}
                  </td>
                  <td style={{ padding: '5px 8px', whiteSpace: 'nowrap', color: 'var(--ink-3)' }}>
                    {fmtTime(log.run_at)}
                  </td>
                  <td style={{ padding: '5px 8px' }}>
                    <span style={statusStyle(log.status)}>{log.status}</span>
                  </td>
                  <td style={{ padding: '5px 8px', color: 'var(--ink-3)', lineHeight: 1.4 }}>
                    {log.message}
                    {log.detail?.added?.length > 0 && (
                      <span style={{ marginLeft: 6, color: 'var(--up-field-ink)', fontSize: 11 }}>
                        +{log.detail.added.join(', ')}
                      </span>
                    )}
                    {log.detail?.removed?.length > 0 && (
                      <span style={{ marginLeft: 6, color: 'var(--down-field-ink)', fontSize: 11 }}>
                        −{log.detail.removed.join(', ')}
                      </span>
                    )}
                    {log.job_id === 'fii_dii_fetch' && log.detail?.source && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ink-3)', opacity: 0.7 }}>
                        [src: {log.detail.source}
                        {log.detail.fii != null && ` · FII stks: ${log.detail.fii}`}
                        {log.detail.dii != null && ` · DII stks: ${log.detail.dii}`}
                        {log.detail.fii_net_fut != null && ` · FII fut: ${log.detail.fii_net_fut}`}
                        {log.detail.fii_pro_fut_opt != null && ` · FII+PRO: ${log.detail.fii_pro_fut_opt}`}
                        {log.detail.client_fut_opt != null && ` · Client: ${log.detail.client_fut_opt}`}
                        ]
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}


/* ══════════════════════════════════════════════════════
   AI Run Log Section
══════════════════════════════════════════════════════ */

// Short display name for model strings
function modelLabel(m) {
  if (!m) return '—';
  if (m.includes('haiku'))  return 'Haiku';
  if (m.includes('sonnet')) return 'Sonnet';
  if (m.includes('opus'))   return 'Opus';
  return m.split('-')[1] || m;
}

const TAB_LABEL = {
  nifty:         'NIFTY',
  dashboard:     'Dashboard',
  market:        'Market',
  scanner:       'Scanner',
  journal:       'Journal',
  performance:   'Performance',
  news_classify: 'News Classify',
  memory:        'Memory Synth',
  unknown:       '?',
};

const TAB_COLOR = {
  nifty:         { bg:'#daeeff', c:'#1060a0' },
  dashboard:     { bg:'#eaf4ea', c:'#1a6a1a' },
  market:        { bg:'#eeedfe', c:'#3838a0' },
  scanner:       { bg:'#faecd8', c:'#7a3e00' },
  journal:       { bg:'#fce4ec', c:'#c62828' },
  performance:   { bg:'#d8f4ec', c:'#0a5a40' },
  news_classify: { bg:'#fff8e1', c:'#f57f17' },
  memory:        { bg:'#f5f5f5', c:'#616161' },
};

const MODEL_COLOR = {
  Haiku:  { bg:'#e8f5e9', c:'#2e7d32' },
  Sonnet: { bg:'#daeeff', c:'#1060a0' },
  Opus:   { bg:'#eeedfe', c:'#3838a0' },
};

function fmtTokens(n) {
  if (!n) return '0';
  if (n >= 1000) return `${(n/1000).toFixed(1)}k`;
  return String(n);
}

function fmtIst(isoUtc) {
  if (!isoUtc) return '—';
  try {
    const d = new Date(isoUtc.replace(' ', 'T') + 'Z');
    return d.toLocaleTimeString('en-IN', { timeZone:'Asia/Kolkata', hour:'2-digit', minute:'2-digit', hour12:false });
  } catch { return isoUtc; }
}

function fmtDateIst(isoUtc) {
  if (!isoUtc) return '—';
  try {
    const d = new Date(isoUtc.replace(' ', 'T') + 'Z');
    return d.toLocaleDateString('en-IN', { timeZone:'Asia/Kolkata', day:'numeric', month:'short' })
         + ' ' + d.toLocaleTimeString('en-IN', { timeZone:'Asia/Kolkata', hour:'2-digit', minute:'2-digit', hour12:false });
  } catch { return isoUtc; }
}

/**
 * Group consecutive news_classify Haiku runs that are within 3 min of each other
 * into a single summarised row. All other runs are displayed individually.
 */
function groupRuns(runs) {
  const grouped = [];
  let i = 0;
  while (i < runs.length) {
    const r = runs[i];
    if (r.tab === 'news_classify') {
      // Look ahead for consecutive news_classify runs within 3 min
      const cluster = [r];
      let j = i + 1;
      while (j < runs.length && runs[j].tab === 'news_classify') {
        const gap = Math.abs(
          new Date(r.created_at.replace(' ', 'T') + 'Z') -
          new Date(runs[j].created_at.replace(' ', 'T') + 'Z')
        ) / 1000; // seconds
        if (gap <= 180) { cluster.push(runs[j]); j++; } else break;
      }
      if (cluster.length > 1) {
        grouped.push({
          _grouped: true,
          count: cluster.length,
          tab: 'news_classify',
          model: cluster[0].model,
          trigger: cluster[0].trigger,
          input_tokens:  cluster.reduce((s, x) => s + (x.input_tokens  || 0), 0),
          output_tokens: cluster.reduce((s, x) => s + (x.output_tokens || 0), 0),
          gen_ms:        cluster.reduce((s, x) => s + (x.gen_ms        || 0), 0),
          status: cluster.every(x => x.status === 'success') ? 'success' : 'error',
          created_at:  cluster[cluster.length - 1].created_at,  // oldest in cluster
          created_end: cluster[0].created_at,                   // newest in cluster
        });
        i = j;
      } else {
        grouped.push(r);
        i++;
      }
    } else {
      grouped.push(r);
      i++;
    }
  }
  return grouped;
}

export function AIRunLogSection() {
  const [runs,    setRuns]    = useState([]);
  const [stats,   setStats]   = useState(null);
  const [days,    setDays]    = useState(7);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedTabs, setSelectedTabs] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAIRunLog(days, 500);
      setRuns(res.runs || []);
      setStats(res.stats || null);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('AIRunLog:', e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s
  useEffect(() => {
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [load]);

  const grouped = useMemo(() => groupRuns(runs), [runs]);

  const filteredRuns = useMemo(() => {
    if (selectedTabs.size === 0) return grouped;
    return grouped.filter(r => selectedTabs.has(r.tab));
  }, [grouped, selectedTabs]);

  const pill = (label, style) => (
    <span style={{ fontSize:11, fontWeight:700, padding:'1px 6px', borderRadius:3,
      background:style?.bg||'var(--surface-3)', color:style?.c||'var(--ink-2)', flexShrink:0 }}>
      {label}
    </span>
  );

  return (
    <SectionCard
      title="AI Run Log"
      subtitle="Every Claude API call — tokens, latency, trigger. Auto-refreshes every 60s."
      defaultOpen={true}
    >
      {/* ── Controls + stats bar ── */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, flexWrap:'wrap' }}>
        {/* Day range selector */}
        <div style={{ display:'flex', gap:4 }}>
          {[1, 3, 7].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              fontSize:11, padding:'3px 9px', borderRadius:10, cursor:'pointer',
              border:'.5px solid var(--hair-strong)',
              background: days===d ? 'var(--amber-ink)' : 'var(--surface)',
              color:       days===d ? '#fff'          : 'var(--ink-2)',
              fontWeight:  days===d ? 700              : 400,
            }}>{d}d</button>
          ))}
        </div>
        <button onClick={load} disabled={loading} style={{
          fontSize:11, padding:'3px 9px', borderRadius:10, cursor:'pointer',
          border:'.5px solid var(--hair-strong)', background:'var(--surface)', color:'var(--ink-2)',
        }}>{loading ? '…' : '↻ Refresh'}</button>
        {lastRefresh && (
          <span style={{ fontSize:11, color:'var(--ink-3)' }}>
            Updated {lastRefresh.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:false })}
          </span>
        )}

        {/* Summary stats */}
        {stats && (
          <div style={{ marginLeft:'auto', display:'flex', gap:14, flexWrap:'wrap' }}>
            <div style={{ fontSize:11, color:'var(--ink-2)' }}>
              <strong style={{ color:'var(--ink)' }}>{stats.total_runs}</strong> runs
            </div>
            <div style={{ fontSize:11, color:'var(--ink-2)' }}>
              <strong style={{ color:'var(--ink)' }}>{fmtTokens(stats.total_input_tokens)}</strong> in
              {' / '}
              <strong style={{ color:'var(--ink)' }}>{fmtTokens(stats.total_output_tokens)}</strong> out tokens
            </div>
          </div>
        )}
      </div>

      {/* ── Per-tab breakdown chips — also act as multi-select filters ── */}
      {stats?.by_tab && stats.by_tab.length > 0 && (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12, alignItems:'center' }}>
          {stats.by_tab.map(t => {
            const tc = TAB_COLOR[t.tab] || { bg:'var(--surface-2)', c:'var(--ink-2)' };
            const isActive = selectedTabs.has(t.tab);
            const hasFilter = selectedTabs.size > 0;
            return (
              <div key={t.tab}
                onClick={() => setSelectedTabs(prev => {
                  const next = new Set(prev);
                  if (next.has(t.tab)) next.delete(t.tab); else next.add(t.tab);
                  return next;
                })}
                style={{ display:'flex', alignItems:'center', gap:5,
                  padding:'4px 9px', borderRadius:6, background:tc.bg,
                  border: isActive ? `1.5px solid ${tc.c}` : `.5px solid ${tc.c}33`,
                  cursor:'pointer', userSelect:'none',
                  opacity: hasFilter && !isActive ? 0.4 : 1,
                  transition:'opacity .12s, border .1s',
                }}>
                <span style={{ fontSize:11, fontWeight:700, color:tc.c }}>{TAB_LABEL[t.tab]||t.tab}</span>
                <span style={{ fontSize:11, color:tc.c, opacity:.8 }}>×{t.cnt}</span>
                <span style={{ fontSize:11, color:tc.c, opacity:.6 }}>
                  {fmtTokens(t.tin)}↑ {fmtTokens(t.tout)}↓
                </span>
              </div>
            );
          })}
          {selectedTabs.size > 0 && (
            <button onClick={() => setSelectedTabs(new Set())} style={{
              fontSize:11, color:'var(--ink-3)', background:'none', border:'none',
              cursor:'pointer', padding:'2px 4px', textDecoration:'underline',
              fontFamily:'var(--font-ui)',
            }}>
              clear
            </button>
          )}
        </div>
      )}

      {/* ── Run list ── */}
      {loading && runs.length === 0 ? (
        <div style={{ fontSize:11, color:'var(--ink-3)', padding:'12px 0' }}>Loading…</div>
      ) : filteredRuns.length === 0 ? (
        <div style={{ fontSize:11, color:'var(--ink-3)', padding:'12px 0' }}>
          {grouped.length === 0
            ? 'No AI runs logged yet. Runs appear here after the first Claude API call.'
            : 'No runs match the selected filter.'}
        </div>
      ) : (
        <div style={{ maxHeight:480, overflowY:'auto', borderRadius:5, border:'.5px solid var(--hair)' }}>
          {/* Header */}
          <div style={{ display:'grid', gridTemplateColumns:'120px 1fr 60px 60px 100px 80px 70px',
            gap:8, padding:'5px 10px', background:'var(--surface-2)',
            fontSize:11, fontWeight:700, color:'var(--ink-3)', textTransform:'uppercase',
            letterSpacing:'.05em', position:'sticky', top:0, zIndex:1 }}>
            <div>Time (IST)</div>
            <div>Tab</div>
            <div>Model</div>
            <div>Trigger</div>
            <div>Tokens In / Out</div>
            <div>Latency</div>
            <div>Status</div>
          </div>

          {filteredRuns.map((r, i) => {
            const ml  = modelLabel(r.model);
            const mc  = MODEL_COLOR[ml] || { bg:'var(--surface-3)', c:'var(--ink-2)' };
            const tc  = TAB_COLOR[r.tab] || { bg:'var(--surface-3)', c:'var(--ink-2)' };
            const isErr = r.status === 'error';
            const isGrouped = r._grouped;
            return (
              <div key={i} style={{ display:'grid',
                gridTemplateColumns:'120px 1fr 60px 60px 100px 80px 70px',
                gap:8, padding:'6px 10px',
                borderTop: i > 0 ? '.5px solid var(--hair)' : 'none',
                background: isErr ? 'rgba(198,40,40,.04)' : 'transparent',
                alignItems:'center',
              }}>
                {/* Time */}
                <div style={{ fontSize:11, color:'var(--ink-2)', fontFamily:'var(--font-data)' }}>
                  {isGrouped && fmtIst(r.created_end) !== fmtIst(r.created_at) ? (
                    <>
                      <div>{fmtIst(r.created_end)}</div>
                      <div style={{ color:'var(--ink-3)', fontSize:11 }}>→ {fmtIst(r.created_at)}</div>
                    </>
                  ) : fmtDateIst(r.created_at)}
                </div>

                {/* Tab + grouped indicator */}
                <div style={{ display:'flex', alignItems:'center', gap:5, minWidth:0 }}>
                  {pill(TAB_LABEL[r.tab] || r.tab, tc)}
                  {isGrouped && (
                    <span style={{ fontSize:11, color:'var(--ink-3)' }}>× {r.count} batches</span>
                  )}
                  {isErr && r.error_msg && (
                    <span style={{ fontSize:11, color:'var(--down-field-ink)', overflow:'hidden',
                      textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:160 }}
                      title={r.error_msg}>
                      ⚠ {r.error_msg}
                    </span>
                  )}
                </div>

                {/* Model */}
                <div>{pill(ml, mc)}</div>

                {/* Trigger */}
                <div>
                  <span style={{ fontSize:11, color: r.trigger==='manual' ? 'var(--amber-ink)' : 'var(--ink-3)' }}>
                    {r.trigger === 'manual' ? '⚡ manual' : '⏱ sched'}
                  </span>
                </div>

                {/* Tokens */}
                <div style={{ fontSize:11, fontFamily:'var(--font-data)', color:'var(--ink-2)' }}>
                  <span style={{ color:'var(--ink-3)' }}>{fmtTokens(r.input_tokens)}</span>
                  {' / '}
                  <span style={{ color:'var(--amber-ink)' }}>{fmtTokens(r.output_tokens)}</span>
                </div>

                {/* Latency */}
                <div style={{ fontSize:11, fontFamily:'var(--font-data)',
                  color: r.gen_ms > 30000 ? 'var(--amber-ink)' : 'var(--ink-2)' }}>
                  {r.gen_ms ? `${(r.gen_ms/1000).toFixed(1)}s` : '—'}
                </div>

                {/* Status */}
                <div style={{ fontSize:11, fontWeight:600,
                  color: isErr ? 'var(--down-field-ink)' : 'var(--up-field-ink)' }}>
                  {isErr ? '✗ error' : '✓ ok'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}


/* ══════════════════════════════════════════════════════
   Section: Index Constituents (NIFTY 50 / SENSEX / BankNIFTY)
══════════════════════════════════════════════════════ */
const INDEX_TABS = [
  { id: 'NIFTY50',   label: 'NIFTY 50' },
  { id: 'SENSEX',    label: 'SENSEX' },
  { id: 'BANKNIFTY', label: 'Bank NIFTY' },
];

export function IndexConstituentsSection() {
  const [activeIdx, setActiveIdx] = useState('NIFTY50');
  const [rows, setRows]           = useState([]);
  const [loadingIdx, setLoadingIdx] = useState(null);
  const [saveStatus, setSaveStatus] = useState({});  // { [symbol]: 'saving'|'ok'|'err' }
  const [seedBusy, setSeedBusy]   = useState(false);
  const [seedMsg, setSeedMsg]     = useState('');

  const loadRows = React.useCallback((idxId) => {
    setLoadingIdx(idxId);
    getMarketMoversConstituents(idxId)
      .then(res => {
        // Embed _orig so handleSave always knows the DB symbol even after the user edits it
        setRows((res.rows ?? []).map(r => ({ ...r, _orig: r.symbol })));
        setLoadingIdx(null);
      })
      .catch(() => setLoadingIdx(null));
  }, []);

  useEffect(() => { loadRows(activeIdx); }, [activeIdx, loadRows]);

  const handleChange = (symbol, field, value) => {
    setRows(prev => prev.map(r => r.symbol === symbol ? { ...r, [field]: value } : r));
  };

  const handleSave = async (row) => {
    // _orig is the symbol that exists in the DB (set on row load, never mutated by edits)
    const origSym = row._orig ?? row.symbol;
    setSaveStatus(s => ({ ...s, [row.symbol]: 'saving' }));
    const newSym = row.symbol !== origSym ? row.symbol : null;
    const res = await updateMarketMoverConstituent(
      activeIdx, origSym, row.company_name, parseFloat(row.weightage), newSym
    );
    const ok = !res?._error;
    setSaveStatus(s => ({ ...s, [row.symbol]: ok ? 'ok' : 'err' }));
    if (ok && newSym) {
      // Symbol was renamed — refresh list so keys are consistent
      setTimeout(() => { setSaveStatus({}); loadRows(activeIdx); }, 1200);
    } else {
      setTimeout(() => setSaveStatus(s => { const n = { ...s }; delete n[row.symbol]; return n; }), 2000);
    }
  };

  const handleSeed = async (force) => {
    setSeedBusy(true);
    setSeedMsg('');
    const res = await seedMarketMoversConstituents(force);
    setSeedBusy(false);
    if (res?.ok) {
      setSeedMsg(`✓ Seeded ${res.seeded} rows (skipped ${res.skipped})`);
      loadRows(activeIdx);
    } else {
      setSeedMsg('✗ Seed failed');
    }
  };

  const thS = {
    padding: '5px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    color: 'var(--ink-3)', background: 'var(--surface-2)', borderBottom: '.5px solid var(--hair)',
    textAlign: 'left', whiteSpace: 'nowrap',
  };
  const tdS = {
    padding: '4px 6px', borderBottom: '.5px solid var(--hair)', verticalAlign: 'middle',
  };

  return (
    <SectionCard
      title="Index Constituents"
      subtitle="Edit tickers and weights for NIFTY 50, SENSEX and Bank NIFTY. Changes apply immediately."
    >
      {/* Index tab selector */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {INDEX_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveIdx(t.id)}
            style={{
              padding: '5px 14px', borderRadius: 5, border: 'none', cursor: 'pointer',
              fontSize: 11, fontWeight: 600,
              background: activeIdx === t.id ? 'var(--amber-ink)' : 'var(--surface-2)',
              color: activeIdx === t.id ? '#fff' : 'var(--ink-2)',
            }}
          >{t.label}</button>
        ))}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Seed controls */}
        <button
          onClick={() => handleSeed(false)}
          disabled={seedBusy}
          style={{ ...BTN({ background: 'var(--surface-2)', color: 'var(--ink-2)' }), opacity: seedBusy ? .6 : 1 }}
        >
          {seedBusy ? 'Seeding…' : 'Seed missing'}
        </button>
        <button
          onClick={() => {
            if (window.confirm('This will wipe and re-seed ALL three indices from hardcoded May 2026 data. Continue?')) {
              handleSeed(true);
            }
          }}
          disabled={seedBusy}
          style={{ ...BTN({ background: '#c0392b', color: '#fff' }), opacity: seedBusy ? .6 : 1 }}
        >
          Force re-seed
        </button>
      </div>

      {seedMsg && (
        <div style={{ fontSize: 11, color: seedMsg.startsWith('✓') ? 'var(--up-field-ink)' : 'var(--down-field-ink)', marginBottom: 10 }}>
          {seedMsg}
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr>
              <th style={{ ...thS, width: 36 }}>#</th>
              <th style={{ ...thS, width: 120 }}>NSE Ticker</th>
              <th style={{ ...thS, minWidth: 150 }}>Company Name</th>
              <th style={{ ...thS, width: 90 }}>Weight (%)</th>
              <th style={{ ...thS, width: 64 }}>Save</th>
            </tr>
          </thead>
          <tbody>
            {loadingIdx === activeIdx
              ? Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {[36,120,150,90,64].map((w, j) => (
                    <td key={j} style={tdS}>
                      <div style={{ height: 9, width: w - 16, borderRadius: 3, background: 'var(--surface-3)', opacity: .5 }} />
                    </td>
                  ))}
                </tr>
              ))
              : rows.length === 0
              ? (
                <tr>
                  <td colSpan={5} style={{ ...tdS, padding: '12px 8px', color: 'var(--ink-3)', textAlign: 'center' }}>
                    No constituents yet. Click "Seed missing" to load defaults.
                  </td>
                </tr>
              )
              : rows.map((r, i) => {
                const st = saveStatus[r.symbol];
                return (
                  <tr key={`${r.symbol}-${i}`} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,.02)' }}>
                    <td style={{ ...tdS, color: 'var(--ink-3)', fontSize: 11, textAlign: 'center' }}>{r.rank_in_idx}</td>
                    {/* Editable NSE ticker */}
                    <td style={tdS}>
                      <input
                        value={r.symbol || ''}
                        onChange={e => handleChange(r.symbol, 'symbol', e.target.value.toUpperCase())}
                        style={{ ...INP, width: '100%', fontSize: 11, padding: '3px 6px', fontFamily: 'var(--font-data)', textTransform: 'uppercase' }}
                        placeholder="NSE symbol"
                      />
                    </td>
                    {/* Editable company name */}
                    <td style={tdS}>
                      <input
                        value={r.company_name || ''}
                        onChange={e => handleChange(r.symbol, 'company_name', e.target.value)}
                        style={{ ...INP, width: '100%', fontSize: 11, padding: '3px 6px' }}
                      />
                    </td>
                    {/* Editable weight */}
                    <td style={tdS}>
                      <input
                        type="number"
                        value={r.weightage || ''}
                        step="0.01"
                        min="0"
                        max="100"
                        onChange={e => handleChange(r.symbol, 'weightage', e.target.value)}
                        style={{ ...INP, width: '100%', fontSize: 11, padding: '3px 6px', textAlign: 'right' }}
                      />
                    </td>
                    {/* Save button */}
                    <td style={{ ...tdS, textAlign: 'center' }}>
                      <button
                        onClick={() => handleSave(r)}
                        disabled={st === 'saving'}
                        style={{
                          padding: '3px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                          fontSize: 11, fontWeight: 600,
                          background: st === 'ok' ? 'var(--up-field-ink)' : st === 'err' ? 'var(--down-field-ink)' : 'var(--surface-3)',
                          color: (st === 'ok' || st === 'err') ? '#fff' : 'var(--ink-2)',
                        }}
                      >
                        {st === 'saving' ? '…' : st === 'ok' ? '✓' : st === 'err' ? '✗' : 'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10 }}>
        Weights should sum to ~100%. Company name is display-only. NSE ticker must match Kite symbol exactly.
      </div>
    </SectionCard>
  );
}


/* ══════════════════════════════════════════════════════
   Section — Data Health (Stage A: read-only display)
══════════════════════════════════════════════════════ */
function HintChip({ hint }) {
  const isAmber = hint === 'likely_bonus_boundary' || hint === 'likely_fresh_or_rename';
  const label = hint === 'likely_bonus_boundary'  ? 'bonus/split boundary'
              : hint === 'likely_fresh_or_rename' ? 'fresh listing / rename'
              : 'spinoff or crash';
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
      background: isAmber ? 'var(--amber-ink)' : 'var(--surface-2)',
      color:      isAmber ? '#fff'             : 'var(--ink-3)',
      letterSpacing: '.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function OnHoldChip() {
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
      background: 'var(--surface-2)', color: 'var(--ink-3)',
      letterSpacing: '.04em', textTransform: 'uppercase', border: '.5px solid var(--hair)',
    }}>ON HOLD</span>
  );
}

function CliffRow({ c, onResolved }) {
  const isOnHold = !c.adjust_allowed || !c.leave_allowed;
  const sym = c.symbol.replace('.NS', '');
  const defaultRatio = c.seam_ratio != null ? c.seam_ratio.toFixed(4) : '';

  const [ratioInput, setRatioInput] = useState(defaultRatio);
  const [busy,       setBusy]       = useState(false);  // POST in flight
  const [rowErr,     setRowErr]     = useState(null);
  const [rowOk,      setRowOk]      = useState(null);   // success message

  const parsedRatio = parseFloat(ratioInput);
  const ratioValid  = !isNaN(parsedRatio) && parsedRatio > 0 && parsedRatio < 1.5;

  const healedMin = (ratioValid && c.pre_ex_min != null) ? (c.pre_ex_min * parsedRatio).toFixed(2) : null;
  const healedMax = (ratioValid && c.pre_ex_max != null) ? (c.pre_ex_max * parsedRatio).toFixed(2) : null;

  const doResolve = async (resolution) => {
    setRowErr(null);
    const label = resolution === 'adjust' ? 'Adjust' : 'Mark crash';
    const confirmed = resolution === 'adjust'
      ? window.confirm(
          `Rescale ALL pre-${c.ex_date} bars of ${sym} by ${parsedRatio.toFixed(4)}?\n` +
          (healedMin && healedMax
            ? `Pre-ex history becomes MIN ₹${healedMin} / MAX ₹${healedMax}.\n`
            : '') +
          `This re-applies on every sync. Proceed?`
        )
      : window.confirm(`Mark ${sym} ${c.ex_date} as a real crash? No price change.`);

    if (!confirmed) return;

    setBusy(true);
    try {
      const body = { symbol: c.symbol, ex_date: c.ex_date, resolution };
      if (resolution === 'adjust') body.ratio = parsedRatio;

      const r = await fetch(`${RAILWAY}/api/corp-actions/resolve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        setRowErr(data.detail || `Error ${r.status}`);
        return;
      }

      const msg = resolution === 'adjust'
        ? `Healed ${data.bars_updated ?? '?'} bars`
        : 'Marked as crash';
      setRowOk(msg);
      setTimeout(() => { onResolved(); }, 1200);
    } catch (e) {
      setRowErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      background: 'var(--surface-2)',
      border: `.5px solid var(--hair)`,
      borderRadius: 5,
      padding: '8px 12px',
      opacity: busy ? 0.7 : 1,
    }}>
      {/* ── Info row ── */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: (isOnHold || c.yf_splits_near || !isOnHold) ? 4 : 0 }}>
        <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink)', minWidth: 90 }}>{sym}</span>
        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{c.ex_date}</span>
        <span style={{ fontSize: 11, color: 'var(--ink)' }}>
          {c.price_before?.toFixed(2)} → {c.price_after?.toFixed(2)}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, color: (c.gap_pct ?? 0) < 0 ? 'var(--dn-ink)' : 'var(--up-ink)' }}>
          {c.gap_pct != null ? `${c.gap_pct > 0 ? '+' : ''}${c.gap_pct.toFixed(1)}%` : '—'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          ratio {c.seam_ratio != null ? c.seam_ratio.toFixed(4) : '—'}
        </span>
        {isOnHold ? <OnHoldChip /> : <HintChip hint={c.classification_hint} />}
      </div>

      {/* ── yf split evidence ── */}
      {c.yf_splits_near && c.yf_splits_near_detail && (
        <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 4 }}>
          yf split ×{c.yf_splits_near_detail.factor} @ {c.yf_splits_near_detail.feed_date}
          {' '}({c.yf_splits_near_detail.cliff_date_gap_days}d from cliff)
        </div>
      )}

      {/* ── Held: notes only, no controls ── */}
      {isOnHold && c.notes && (
        <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{c.notes}</div>
      )}

      {/* ── Actionable: ratio input + predict + buttons ── */}
      {!isOnHold && !rowOk && (
        <div style={{ marginTop: 6 }}>
          {/* Ratio input */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 11, color: 'var(--ink-3)' }}>ratio</label>
            <input
              type="text"
              value={ratioInput}
              onChange={e => setRatioInput(e.target.value)}
              disabled={busy}
              style={{
                width: 80, fontSize: 11, padding: '2px 6px',
                background: 'var(--surface-1)', color: 'var(--ink)',
                border: `.5px solid ${ratioValid ? 'var(--hair)' : 'var(--dn-ink)'}`,
                borderRadius: 3, outline: 'none',
              }}
            />
            {!ratioValid && ratioInput !== '' && (
              <span style={{ fontSize: 10, color: 'var(--dn-ink)' }}>ratio must be 0–1.5</span>
            )}
          </div>

          {/* Live predicted scale */}
          <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 4 }}>
            {ratioValid
              ? (c.pre_ex_min != null && c.pre_ex_max != null
                  ? `pre-ex history → MIN ₹${healedMin} / MAX ₹${healedMax} — confirm this matches the adjusted chart before adjusting`
                  : 'pre-ex range unavailable')
              : 'enter a valid ratio to preview healed scale'
            }
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              disabled={busy || !ratioValid}
              onClick={() => doResolve('adjust')}
              style={{
                ...BTN(),
                background: ratioValid && !busy ? 'var(--accent)' : 'var(--surface-3)',
                color: ratioValid && !busy ? '#fff' : 'var(--ink-3)',
                border: 'none', cursor: busy || !ratioValid ? 'not-allowed' : 'pointer',
              }}>
              {busy ? 'Healing… recomputing RS' : 'Adjust'}
            </button>
            <button
              disabled={busy}
              onClick={() => doResolve('leave')}
              style={{
                ...BTN(),
                background: 'var(--surface-2)', color: 'var(--ink)',
                border: '.5px solid var(--hair)', cursor: busy ? 'not-allowed' : 'pointer',
              }}>
              Mark crash (leave)
            </button>
          </div>

          {/* Row-level error */}
          {rowErr && (
            <div style={{ fontSize: 11, color: 'var(--dn-ink)', marginTop: 6 }}>{rowErr}</div>
          )}
        </div>
      )}

      {/* ── Post-success confirmation (shown briefly before row is removed) ── */}
      {rowOk && (
        <div style={{ fontSize: 11, color: 'var(--up-ink)', marginTop: 6 }}>{rowOk}</div>
      )}
    </div>
  );
}

// ── Volume storage section (Settings -> Data) ────────────────────────────────
//
// Nothing in the app reported free space until FIX 34. The failure mode is not
// a degraded feature: it is SQLite failing mid-write during a capture sweep, on
// option data that cannot be re-fetched once the contract expires.
//
// The burn rate is MEASURED from volume_space_log (one reading a day, written
// by the 03:10 scheduler job). With fewer than three readings the backend
// returns burn.status = 'insufficient_history' and no fill date, and this
// renders that verbatim rather than extrapolating a date from one sample.
const _MB = n => (n == null ? '—' : `${(n / 1e6).toLocaleString('en-IN', { maximumFractionDigits: 1 })} MB`);

export function StorageSection() {
  const [data, setData] = useState(undefined);   // undefined = loading
  const [err,  setErr]  = useState(null);

  const load = () => {
    setData(undefined); setErr(null);
    fetch(`${RAILWAY}/api/data-health/storage`, { credentials: 'include' })
      .then(r => {
        if (r.status === 401 || r.status === 403) throw new Error('Not authorized');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => setData(d))
      .catch(e => { setData(null); setErr(e.message); });
  };
  useEffect(() => { load(); }, []);

  const reading = data?.reading;
  const burn    = data?.burn;
  const proj    = data?.projection;
  const arch    = data?.archives;
  const prune   = data?.prune;
  const oi      = data?.oi;
  const level   = proj?.level || null;

  const tone = level === 'critical'
    ? { bg: 'var(--down-field-bg)', ink: 'var(--down-field-ink)', label: 'CRITICAL' }
    : level === 'warn'
      ? { bg: 'var(--amber-bg)', ink: 'var(--amber-ink)', label: 'WARNING' }
      : null;

  const pct = proj?.pct_free != null ? proj.pct_free * 100 : null;

  return (
    <SectionCard
      title={pct != null ? `Storage (${pct.toFixed(0)}% free)` : 'Storage'}
      subtitle="Data volume free space · measured burn rate · projected fill date"
      defaultOpen={!!level}>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button
          onClick={load}
          style={{ ...BTN(), background: 'var(--surface-2)', color: 'var(--ink)', border: '.5px solid var(--hair)' }}>
          ↻ Refresh
        </button>
      </div>

      {data === undefined ? (
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Loading…</div>
      ) : err ? (
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{err}</div>
      ) : !reading || reading.free_bytes == null ? (
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          Could not read the volume{reading?.error ? ` — ${reading.error}` : ''}.
        </div>
      ) : (
        <>
          {tone && (
            <div style={{
              background: tone.bg, color: tone.ink, borderRadius: 6,
              padding: '8px 11px', marginBottom: 14,
              fontSize: 11, fontWeight: 600, lineHeight: 1.5,
            }}>
              {tone.label} — {(proj.reasons || []).join('; ')}
            </div>
          )}

          {/* ── Space ── */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>
              Volume · {reading.mount}
            </div>
            <StorageRow label="Free"  value={`${_MB(reading.free_bytes)}${pct != null ? `  (${pct.toFixed(0)}%)` : ''}`} strong />
            <StorageRow label="Used"  value={_MB(reading.used_bytes)} />
            <StorageRow label="Total" value={_MB(reading.total_bytes)} />
            {(reading.files || []).filter(f => f.exists).map(f => (
              <StorageRow
                key={f.name}
                label={f.name}
                value={`${_MB(f.total)}${f.wal ? `  (WAL ${_MB(f.wal)})` : ''}`}
                muted
              />
            ))}
          </div>

          {/* ── Archive (FIX 35 Phase 2a) ── */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>
              Archive
            </div>
            {!arch ? (
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>No archive data.</div>
            ) : !arch.configured ? (
              <div style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                Object store not configured — missing {(arch.missing_env || []).join(', ')}.
                Nothing is being archived; nothing is failing either.
              </div>
            ) : (
              <>
                {arch.last_run?.status === 'auth_failed' && (
                  <div style={{
                    background: 'var(--down-field-bg)', color: 'var(--down-field-ink)',
                    borderRadius: 6, padding: '8px 11px', marginBottom: 10,
                    fontSize: 11, fontWeight: 600, lineHeight: 1.5,
                  }}>
                    CREDENTIALS REJECTED — archiving has stopped. {arch.last_run.error}
                  </div>
                )}
                <StorageRow label="Archives verified" value={`${arch.verified}`} strong />
                <StorageRow label="Bytes in the store" value={_MB(arch.bytes_in_store)} />
                <StorageRow
                  label="Local bytes pending reclamation"
                  value={`${_MB(arch.reclaimable_bytes)} (est.)`}
                  strong
                />
                {arch.reclaimable_rows > 0 && (
                  <StorageRow label="Rows safe to delete"
                              value={arch.reclaimable_rows.toLocaleString('en-IN')} muted />
                )}
                {arch.written_unverified > 0 && (
                  <StorageRow label="Written but NOT verified"
                              value={`${arch.written_unverified}`} muted />
                )}
                {arch.failed > 0 && (
                  <StorageRow label="Failed (retried next run)" value={`${arch.failed}`} muted />
                )}
                <StorageRow label="Eligible right now" value={`${arch.eligible_now}`} muted />
                <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 6, lineHeight: 1.5 }}>
                  Reclamation is an estimate at ~110 B/row plus 1.66x for indexes. Nothing is
                  deleted yet — verified archives are what a later deletion pass is allowed to
                  act on.
                  {arch.last_run ? ` Last run ${arch.last_run.started_at} (${arch.last_run.status}).` : ''}
                </div>
              </>
            )}
          </div>

          {/* ── OI snapshots (FIX 37 Phase 2c) ── */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>
              OI snapshots
            </div>
            {!oi ? (
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>No OI archive data.</div>
            ) : !oi.configured ? (
              <div style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                Object store not configured — per-minute CE/PE open interest is not
                being archived.
              </div>
            ) : (
              <>
                <StorageRow label="Dates archived" value={`${oi.verified_dates}`} strong />
                <StorageRow label="Bytes in the store" value={_MB(oi.bytes_in_store)} />
                <StorageRow label="Rows in the store"
                            value={(oi.rows_in_store || 0).toLocaleString('en-IN')} muted />
                <StorageRow label="Dates held locally" value={`${oi.local_dates}`} />
                <StorageRow label="Oldest local" value={oi.oldest_local || '—'} muted />
                <StorageRow label="Eligible to archive" value={`${oi.eligible_now}`} muted />
                <StorageRow label="Prunable now" value={`${oi.prunable_now}`} muted />
                {oi.failed_objects > 0 && (
                  <StorageRow label="Failed objects (retried)" value={`${oi.failed_objects}`} muted />
                )}
                <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 6, lineHeight: 1.5 }}>
                  {oi.local_retention_days} days are kept locally for the live read paths;
                  everything older is archived and then deleted. Both objects for a date
                  must verify before it counts as archived.
                </div>
              </>
            )}
          </div>

          {/* ── Reclamation (FIX 36 Phase 2b) ── */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>
              Reclamation
            </div>
            {!prune ? (
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>No prune data.</div>
            ) : (
              <>
                <StorageRow
                  label="auto_vacuum · options_bars.db"
                  value={prune.auto_vacuum === 2 ? 'INCREMENTAL' :
                         prune.auto_vacuum === 1 ? 'FULL' : 'NONE — conversion pending'}
                  strong={prune.auto_vacuum !== 2}
                />
                {oi?.terminal_db && (
                  <StorageRow
                    label="auto_vacuum · terminal.db"
                    value={oi.terminal_db.auto_vacuum === 2 ? 'INCREMENTAL' :
                           oi.terminal_db.auto_vacuum === 1 ? 'FULL' : 'NONE — conversion pending'}
                    strong={oi.terminal_db.auto_vacuum !== 2}
                  />
                )}
                <StorageRow label="Groups pruned" value={`${prune.groups_pruned_total}`} />
                <StorageRow label="Rows deleted"
                            value={(prune.rows_deleted_total || 0).toLocaleString('en-IN')} />
                <StorageRow label="Disk reclaimed" value={_MB(prune.bytes_reclaimed_total)} strong />
                <StorageRow label="Prunable now" value={`${prune.prunable_now}`} muted />
                <StorageRow label="Blocked" value={`${prune.blocked_now}`} muted />
                {prune.auto_vacuum !== 2 && prune.vacuum_check && !prune.vacuum_check.ok && (
                  <div style={{ fontSize: 10, color: 'var(--amber-ink)', marginTop: 6, lineHeight: 1.5 }}>
                    The one-time VACUUM is being refused: {prune.vacuum_check.reason}.
                    Until it runs, deleted rows sit on the freelist and the file does not shrink.
                  </div>
                )}
                {prune.auto_vacuum !== 2 && prune.db && (
                  <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 6, lineHeight: 1.5 }}>
                    {_MB(prune.db.freelist_bytes)} is on the freelist and will come back when the
                    conversion runs. Predicted file size afterwards: {_MB(prune.db.predicted_after_vacuum)}.
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Burn rate and projection ── */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>
              Burn rate
            </div>
            {burn?.status === 'ok' ? (
              <>
                {/* D5: gross is what capture writes, net is what the volume
                    actually loses. The fill date comes from net; the gap
                    between them is how much archiving is buying back. */}
                <StorageRow
                  label="Gross — written by capture"
                  value={`${_MB(proj.gross_bytes_per_session)} / session`}
                  strong
                />
                <StorageRow
                  label="Net — after reclamation (mean)"
                  value={`${_MB(proj.net_bytes_per_session)} / session`}
                  strong
                />
                {/* 013: the runway is projected from the median-based net so a
                    one-off step in the window (an import, a backup directory)
                    cannot pass for a rate. Both are shown; the note says when
                    they disagree. */}
                {proj.median_used && (
                  <StorageRow
                    label="Net used for runway (median-based)"
                    value={`${_MB(proj.runway_net_bytes_per_session)} / session`}
                    strong
                  />
                )}
                {proj.oneoff_suspected && (
                  <div style={{ fontSize: 11, color: 'var(--ink-2)', fontWeight: 600, padding: '4px 0', lineHeight: 1.5 }}>
                    {proj.oneoff_note}
                  </div>
                )}
                {proj.reclamation_keeping_up ? (
                  <div style={{ fontSize: 11, color: 'var(--up-field-ink)', fontWeight: 600, padding: '4px 0' }}>
                    Reclamation is outpacing capture — no fill date at the current net rate.
                  </div>
                ) : (
                  <>
                    <StorageRow
                      label="Runway"
                      value={proj.sessions_remaining != null ? `${proj.sessions_remaining} session(s)` : '—'}
                    />
                    <StorageRow label="Projected full" value={proj.fill_date || '—'} />
                  </>
                )}
                <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 6, lineHeight: 1.5 }}>
                  {/* D2: a rate from four of fourteen intervals must not look
                      like one from fourteen. */}
                  Gross measured over {burn.intervals - burn.intervals_excluded} of{' '}
                  {burn.intervals} interval(s)
                  {burn.intervals_excluded > 0
                    ? ` — ${burn.intervals_excluded} excluded as reclamation day${burn.intervals_excluded === 1 ? '' : 's'} (${(burn.excluded_dates || []).join(', ')})`
                    : ''}
                  {'. '}
                  Measured over {burn.readings} reading(s) from {burn.from_date} to {burn.to_date}
                  {burn.sessions ? ` (${burn.sessions} ${burn.session_source === 'weekdays' ? 'weekday' : 'trading session'}${burn.sessions === 1 ? '' : 's'})` : ''}
                  {burn.session_source === 'sessions'
                    ? ', counted from days that actually have captured bars — holidays excluded.'
                    : '.'}
                  {burn.session_note ? ` ${burn.session_note}.` : ''}
                  {burn.note ? ` ${burn.note}.` : ''}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                <span style={{ fontWeight: 700, color: 'var(--ink-2)' }}>Insufficient history</span>
                {' — '}
                {burn?.note || 'not enough readings to measure a rate'}.
                <div style={{ marginTop: 4 }}>
                  No fill date is projected from a single sample. One reading is taken
                  each day at 03:10 IST.
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </SectionCard>
  );
}

function StorageRow({ label, value, strong = false, muted = false }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      padding: '3px 0',
    }}>
      <div style={{ fontSize: muted ? 10 : 11, color: muted ? 'var(--ink-3)' : 'var(--ink-2)' }}>{label}</div>
      <div style={{
        fontSize: muted ? 10 : 11, fontVariantNumeric: 'tabular-nums',
        fontWeight: strong ? 700 : 500,
        color: muted ? 'var(--ink-3)' : 'var(--ink-1)',
      }}>{value}</div>
    </div>
  );
}

export function DataHealthSection() {
  // Each fetch has independent state so groups render as soon as their own data arrives.
  const [pending,  setPending]  = useState(undefined);  // undefined = loading
  const [uDiff,    setUDiff]    = useState(undefined);
  const [stale,    setStale]    = useState(undefined);
  // pErr/dErr/sErr: null=ok, string=error message
  const [pErr, setPErr] = useState(null);
  const [dErr, setDErr] = useState(null);
  const [sErr, setSErr] = useState(null);

  const loadPending = () => {
    setPending(undefined); setPErr(null);
    fetch(`${RAILWAY}/api/corp-actions/pending`, { credentials: 'include' })
      .then(r => {
        if (r.status === 401 || r.status === 403) throw new Error('Not authorized');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => setPending(d))
      .catch(e => { setPending(null); setPErr(e.message); });
  };

  const loadUDiff = () => {
    setUDiff(undefined); setDErr(null);
    fetch(`${RAILWAY}/api/data-health/universe-diff`, { credentials: 'include' })
      .then(r => {
        if (r.status === 401 || r.status === 403) throw new Error('Not authorized');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => setUDiff(d))
      .catch(e => { setUDiff(null); setDErr(e.message); });
  };

  const loadStale = () => {
    setStale(undefined); setSErr(null);
    fetch(`${RAILWAY}/api/data-health/stale`, { credentials: 'include' })
      .then(r => {
        if (r.status === 401 || r.status === 403) throw new Error('Not authorized');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => setStale(d))
      .catch(e => { setStale(null); setSErr(e.message); });
  };

  const load = () => { loadPending(); loadUDiff(); loadStale(); };

  useEffect(() => { load(); }, []);

  // Derived values — undefined while loading, fall back gracefully
  const candidates   = pending?.candidates ?? [];
  const pendingCount = pending?.count;          // undefined until loaded
  const dropped      = uDiff?.dropped  ?? [];
  const added        = uDiff?.added    ?? [];
  const staleList    = stale?.stale    ?? [];

  const title = pendingCount != null ? `Data Health (${pendingCount})` : 'Data Health';

  const Spinner = () => (
    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Loading…</div>
  );
  const ErrLine = ({ msg }) => (
    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{msg}</div>
  );

  return (
    <SectionCard
      title={title}
      subtitle="Corporate actions · universe diff · stale symbols"
      defaultOpen={pendingCount > 0}>

      {/* ── Refresh button ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button
          onClick={load}
          style={{ ...BTN(), background: 'var(--surface-2)', color: 'var(--ink)', border: '.5px solid var(--hair)' }}>
          ↻ Refresh
        </button>
      </div>

      {/* ── GROUP 1: Cliffs awaiting review ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>
          Cliffs awaiting review{pendingCount != null ? ` (${pendingCount})` : ''}
        </div>
        {pending === undefined ? <Spinner /> : pErr ? <ErrLine msg={pErr} /> :
          candidates.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>No cliffs awaiting review.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {candidates.map(c => (
                <CliffRow key={`${c.symbol}_${c.ex_date}`} c={c} onResolved={loadPending} />
              ))}
            </div>
          )
        }
      </div>

      {/* ── GROUP 2: Universe changes ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>
          Universe changes
        </div>
        {uDiff === undefined ? <Spinner /> : dErr ? <ErrLine msg={dErr} /> :
          dropped.length === 0 && added.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>No universe changes since last sync.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dropped.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 4 }}>Dropped ({dropped.length})</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {dropped.map(s => (
                      <span key={s} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 3, background: 'var(--surface-2)', border: '.5px solid var(--hair)', color: 'var(--ink-3)' }}>
                        {s.replace('.NS', '')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {added.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 4 }}>Added ({added.length})</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {added.map(s => (
                      <span key={s} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 3, background: 'var(--surface-2)', border: '.5px solid var(--hair)', color: 'var(--ink)' }}>
                        {s.replace('.NS', '')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        }
        <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 8 }}>
          Tracked: {uDiff?.current_count ?? '—'} · Retained: {uDiff?.retained_count ?? '—'}
        </div>
      </div>

      {/* ── GROUP 3: Stale symbols ── */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>
          Stale symbols
        </div>
        {stale === undefined ? <Spinner /> : sErr ? <ErrLine msg={sErr} /> :
          staleList.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>All tracked symbols current.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {staleList.map(s => (
                <div key={s.symbol} style={{
                  display: 'grid', gridTemplateColumns: '120px 100px 1fr',
                  gap: 8, alignItems: 'center',
                  padding: '4px 8px',
                  background: 'var(--surface-2)', borderRadius: 4,
                  border: '.5px solid var(--hair)',
                }}>
                  <span style={{ fontWeight: 700, fontSize: 11, color: 'var(--ink)' }}>
                    {s.symbol.replace('.NS', '')}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.last_bar}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: s.staleness_days > 14 ? 'var(--amber-ink)' : 'var(--ink-3)' }}>
                    {s.staleness_days}d behind
                  </span>
                </div>
              ))}
            </div>
          )
        }
        {stale?.universe_max_bar && (
          <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 6 }}>
            universe max bar: {stale.universe_max_bar}
          </div>
        )}
      </div>

    </SectionCard>
  );
}


/* ══════════════════════════════════════════════════════
   Main Admin Tab
══════════════════════════════════════════════════════ */
export default function AdminTab() {
  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>Admin</div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          Owner-only. Data entered here is saved directly to the database.
        </div>
      </div>

      <AIRunLogSection />
      <IndexConstituentsSection />
      <SectorAssignmentsSection />
      <MacroEventsSection />
      <FiiDiiSection />
      <EiaForecastSection />
      <DataSourcesSection />
      <SchedulerLogsSection />
      <StorageSection />
      <DataHealthSection />
    </div>
  );
}
